import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";

export default function C_E_TEXTAREA({
  tempValue,
  onChange,
  onBlur,
  onKeyDown,
  rowId,
  fieldKey,
  inPopup = false, // Added inPopup prop
}) {
  const [pos, setPos] = useState({ top: 0, left: 0, width: 200 });
  const [isFocused, setIsFocused] = useState(false); // Added for popup helper text
  const textareaRef = useRef(null);

  const properties_state = useSelector((state) => state.properties);

  // 1. Calculate precise position on screen to float perfectly over the cell
  // (Skips execution if rendering inside a popup)
  useLayoutEffect(() => {
    if (inPopup) return;

    const cell = document.querySelector(
      `[data-row-id="${rowId}"][data-field-key="${fieldKey}"]`,
    );
    if (cell) {
      const rect = cell.getBoundingClientRect();
      setPos({
        top: rect.top - 4, // Slight offset for the border
        left: rect.left,
        width: Math.max(200, rect.width), // Minimum 200px, but matches cell if wider
      });
    }
  }, [rowId, fieldKey, inPopup]);

  // 2. Auto-grow the textarea as the user types
  // (Disabled in popup mode to strictly maintain the 3-row layout)
  useEffect(() => {
    if (!inPopup && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [tempValue, inPopup]);

  // 3. Intercept keys
  const handleKeyDownLocal = (e) => {
    if (e.key === "Escape") {
      if (inPopup) e.target.blur(); // Force blur in popup
      if (onKeyDown) onKeyDown(e); // Let TableBody handle cancel
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (inPopup) e.target.blur(); // Force blur in popup to hide helper text
      // Pass the Enter key up to TableBody so it triggers its own save logic natively
      if (onKeyDown) onKeyDown(e);
    } else if (e.key === "Enter" && e.shiftKey) {
      // Shift+Enter creates a new line, stop propagation so grid doesn't intercept
      e.stopPropagation();
    } else {
      // Stop arrows from moving grid focus while typing in the textarea
      e.stopPropagation();
    }
  };

  // 4. POPUP MODE
  // Renders inline standard layout with exactly 3 rows
  if (inPopup) {
    const mode_map = properties_state.mode_map; //editor || viewer

    return (
      <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <textarea
          disabled={mode_map === "viewer"}
          ref={textareaRef}
          value={tempValue}
          onChange={onChange}
          onBlur={(e) => {
            setIsFocused(false);
            if (onBlur) onBlur(e);
          }}
          onKeyDown={handleKeyDownLocal}
          onFocus={() => setIsFocused(true)}
          className="input"
          rows={3} // Strictly 3 rows
          style={{
            width: "100%",
            boxSizing: "border-box",
            resize: "vertical", // Allows manual resizing if needed
            padding: "8px",
            fontFamily: "inherit",
            fontSize: "inherit",
            lineHeight: "1.5",
          }}
        />
        {isFocused && (
          <p
            className="text_small"
            style={{ margin: "4px 0 0 0", color: "#666", fontSize: "12px" }}
          >
            <strong>Enter</strong> to save, <strong>Shift+Enter</strong> for new
            line
          </p>
        )}
      </div>
    );
  }

  // 5. PORTAL EDIT MODE
  // Mounts exactly at the body level to escape cell clipping
  return ReactDOM.createPortal(
    <div
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        zIndex: 99999, // Float above headers and menus
        minWidth: "200px",
        maxWidth: "calc(100vw - 50px)",
        width: pos.width,
        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        borderRadius: "4px",
        background: "#fff",
        border: "2px solid #6666ff", // Highlights that it is active
      }}
    >
      <textarea
        ref={textareaRef}
        value={tempValue} // Directly tethered to TableBody
        onChange={onChange} // Directly updates TableBody state on every keystroke
        onBlur={onBlur} // TableBody handles closing
        onKeyDown={handleKeyDownLocal}
        autoFocus
        rows={3} // Sets base row height before auto-grow takes over
        style={{
          width: "100%",
          resize: "none",
          outline: "none",
          border: "none",
          background: "transparent",
          padding: "8px",
          fontFamily: "inherit",
          fontSize: "inherit",
          lineHeight: "1.5",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      />
      <div
        style={{
          fontSize: "10px",
          color: "#888",
          textAlign: "right",
          padding: "2px 8px 6px",
          background: "#f9f9f9",
          borderBottomLeftRadius: "4px",
          borderBottomRightRadius: "4px",
        }}
      >
        <strong>Enter</strong> to save, <strong>Shift+Enter</strong> for new
        line
      </div>
    </div>,
    document.body,
  );
}
